import { ChangeEvent, ForwardedRef, forwardRef, useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, useFormikContext } from 'formik'
import { cleanHTML } from '../utils/html'
import { Market } from '../types/booking'
import { OverviewValues } from '../types/overview'
import { TERMS } from '../utils/terms'
import classNames from 'classnames'

function encodeLink (link: string): string {
  return link.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

export default forwardRef(function Terms ({ market }: { market: Market }, ref: ForwardedRef<HTMLElement>): JSX.Element {
  const id = useId()
  const { t } = useTranslation()
  const { setFieldValue, validateField, errors } = useFormikContext<OverviewValues>()
  const terms = TERMS[market]
  const termErrors: Record<typeof terms[number], string> | undefined = errors?.terms as Record<typeof terms[number], string> | undefined
  const termsErrors = Array.from(new Set(terms.map(term => termErrors?.[term]).filter(term => term != null)))
  const singleCheckbox = market === 'kootstra'

  const handleChange = useCallback(async (e: ChangeEvent<HTMLInputElement>, index: number) => {
    if (singleCheckbox) {
      await Promise.all(terms.map(async (term) => await setFieldValue(`terms.${term}`, e.currentTarget.checked, false)))
      await Promise.all(terms.map(async (term) => await validateField(`terms.${term}`)))
    } else {
      await setFieldValue(`terms.${terms[index]}`, e.currentTarget.checked, true)
    }
  }, [singleCheckbox, setFieldValue, terms, validateField])

  return (
    <section className='bg-brand-well px-8 py-10 flex flex-col gap-4 select-none' ref={ref}>
      {termsErrors.map(error => (
        <div className='text-red-700 has-error scroll-mt-4' key={error}>
          {error}
        </div>
      ))}
      {terms.map((term, index) => (
        <div className='group flex gap-4' key={term}>
          <div>
            <Field
              type='checkbox' name={`terms.${term}`} id={`${id}-${term}`}
              className={classNames('text-brand-primary focus:ring-brand-primary', { invisible: singleCheckbox && index > 0 })}
              onChange={async (e: ChangeEvent<HTMLInputElement>) => await handleChange(e, index)}
            />
          </div>
          <label htmlFor={`${id}-${term}`}>
            <strong>{t(`terms:${term}.title`)}</strong>
            <div dangerouslySetInnerHTML={{
              __html: cleanHTML(t(`terms:${term}.description`, {
                link: encodeLink(t(`terms:${term}.link`)),
                link2: encodeLink(t(`terms:${term}.link2`))
              }) ?? '')
            }}
            />
          </label>
        </div>
      ))}
    </section>
  )
})
